// src/components/PrintableTable/PrintableTable.jsx

import React, { useRef, useState } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { AiOutlineDownload, AiOutlinePrinter, AiOutlineSetting, AiOutlineClose } from 'react-icons/ai';
import { BsColumnsGap } from 'react-icons/bs';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './PrintableTable.css';

export default function PrintableTable({
  children,             // your <table> element
  fileName = 'table.pdf',
  title = 'Data Export',
  showOptions = true,
  data
}) {
  const containerRef = useRef();
  const [showSettings, setShowSettings] = useState(false);
  const [pdfSettings, setPdfSettings] = useState({
    orientation: 'landscape',
    format: 'a4',
    fontSize: 10,
    includeTitle: true,
    includeDate: true,
    maxRowsPerPage: 50
  });
  const [showSaveDropdown, setShowSaveDropdown] = useState(false);
  const [showFileNamePrompt, setShowFileNamePrompt] = useState(false);
  const [pendingExportType, setPendingExportType] = useState(null); // 'pdf' or 'excel'
  const [customFileName, setCustomFileName] = useState('');
  const [showFieldsModal, setShowFieldsModal] = useState(false);
  const [selectedFields, setSelectedFields] = useState([]);
  const [allFields, setAllFields] = useState([]);

  // Extract available fields from table headers
  React.useEffect(() => {
    const tableEl = containerRef.current?.querySelector('table');
    if (!tableEl) return;
    const headerRow = tableEl.querySelector('thead tr');
    if (!headerRow) return;

    const headerCells = Array.from(headerRow.querySelectorAll('th'));
    const fields = [];
    let visibleIndex = -1;

    headerCells.forEach((cell, rawIndex) => {
      if (cell.classList.contains('no-print')) return;
      visibleIndex += 1;
      const labelText = cell.textContent.trim() || `Column ${visibleIndex + 1}`;
      const dataKey =
        cell.getAttribute('data-field-key') ||
        `${labelText.replace(/\s+/g, '_').toLowerCase()}_${visibleIndex}`;
      fields.push({
        key: dataKey,
        label: labelText,
        visibleIndex,
      });
    });

    setAllFields(fields);
    setSelectedFields(prev => {
      if (!prev.length) {
        return fields.map(field => field.key);
      }
      const validPrev = fields
        .filter(field => prev.includes(field.key))
        .map(field => field.key);
      return validPrev.length ? validPrev : fields.map(field => field.key);
    });
  }, [children]);

  // Print dialog (CSS @media print handles hiding everything but this table)
  const handlePrint = () => {
    window.print();
  };

  // File name prompt logic
  const handleExportWithPrompt = (type) => {
    setPendingExportType(type);
    setCustomFileName('');
    setShowFileNamePrompt(true);
    setShowSaveDropdown(false);
  };

  const handleFileNameSubmit = (e) => {
    e.preventDefault();
    let name = customFileName.trim();
    if (!name) name = title.replace(/\s+/g, '_').toLowerCase();
    if (pendingExportType === 'pdf' && !name.endsWith('.pdf')) name += '.pdf';
    if (pendingExportType === 'excel' && !name.endsWith('.csv')) name += '.csv';
    setShowFileNamePrompt(false);
    if (pendingExportType === 'pdf') handleSavePDF(name);
    if (pendingExportType === 'excel') handleExportExcel(name);
  };

  // PDF export
  const handleSavePDF = (customName) => {
    try {
      console.log('Starting PDF generation...');
      
    const doc = new jsPDF({
        orientation: pdfSettings.orientation,
      unit: 'pt',
        format: pdfSettings.format
    });

    // find the table node inside our wrapper
    const tableEl = containerRef.current.querySelector('table');
      
      if (!tableEl) {
        console.error('Table element not found');
        alert('Table not found. Please try again.');
        return;
      }

      console.log('Table element found:', tableEl);

      const activeFields = allFields.filter(field => selectedFields.includes(field.key));
      if (!activeFields.length) {
        toast.error('Select at least one field to export.', { autoClose: 3000 });
        return;
      }

      const headers = activeFields.map(field => field.label);
      console.log('Extracted headers:', headers);

      const dataRows = Array.from(tableEl.querySelectorAll('tbody tr'));
      const data = dataRows
        .map(row => {
          const visibleCells = Array.from(row.querySelectorAll('td')).filter(
            cell => !cell.classList.contains('no-print')
          );
          return activeFields.map(field => {
            const cell = visibleCells[field.visibleIndex];
            return cell ? cell.textContent.trim() : '';
          });
        })
        .filter(row => row.some(value => value !== ''));

      console.log('Extracted data:', data);

      // Add title and date if enabled
      let startY = 40;
      if (pdfSettings.includeTitle) {
        doc.setFontSize(16);
        doc.text(title, 40, startY);
        startY += 30;
      }
      
      if (pdfSettings.includeDate) {
        doc.setFontSize(10);
        doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 40, startY);
        startY += 20;
      }

      // Generate table
      if (data.length === 0) {
        // If no data, just show the title
        doc.setFontSize(12);
        doc.text('No data found', 40, startY + 20);
      } else {
        autoTable(doc, {
          head: [headers],
          body: data,
          startY: startY,
          styles: { 
            fontSize: pdfSettings.fontSize,
            cellPadding: 5
          },
          headStyles: {
            fillColor: [18, 58, 140],
            textColor: 255,
            fontStyle: 'bold'
          },
          alternateRowStyles: {
            fillColor: [245, 245, 245]
          },
          margin: { top: startY, right: 40, bottom: 40, left: 40 },
          pageBreak: 'auto',
        });
      }

      doc.save(customName || fileName);
      console.log('PDF generated successfully!');
      toast.success('PDF saved successfully!', { autoClose: 2000 });
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Error generating PDF. Please try again.', { autoClose: 3000 });
    }
  };

  // Excel export
  const handleExportExcel = (customName) => {
    try {
      console.log('Starting Excel export...');
      
      const tableEl = containerRef.current.querySelector('table');
      
      if (!tableEl) {
        console.error('Table element not found');
        alert('Table not found. Please try again.');
        return;
      }

      const activeFields = allFields.filter(field => selectedFields.includes(field.key));
      if (!activeFields.length) {
        toast.error('Select at least one field to export.', { autoClose: 3000 });
        return;
      }

      const headers = activeFields.map(field => field.label);

      const dataRows = Array.from(tableEl.querySelectorAll('tbody tr'));
      const data = dataRows
        .map(row => {
          const visibleCells = Array.from(row.querySelectorAll('td')).filter(
            cell => !cell.classList.contains('no-print')
          );
          return activeFields.map(field => {
            const cell = visibleCells[field.visibleIndex];
            return cell ? cell.textContent.trim() : '';
          });
        })
        .filter(row => row.some(value => value !== ''));

      // Create CSV content with BOM for Excel compatibility
      const BOM = '\uFEFF';
      let csvContent = BOM;
      
      // Add title if enabled
      if (pdfSettings.includeTitle) {
        csvContent += `${title}\n`;
      }
      
      // Add date if enabled
      if (pdfSettings.includeDate) {
        csvContent += `Generated on: ${new Date().toLocaleDateString()}\n\n`;
      }
      
      // Add headers
      csvContent += headers.join(',') + '\n';
      
      // Add data with proper escaping
      data.forEach(row => {
        const escapedRow = row.map(cell => {
          // Handle special characters and commas
          const escaped = cell.replace(/"/g, '""');
          return `"${escaped}"`;
        });
        csvContent += escapedRow.join(',') + '\n';
      });

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', customName || fileName.replace('.pdf', '.csv'));
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the URL object
      setTimeout(() => URL.revokeObjectURL(url), 100);
      
      console.log('Excel export completed successfully!');
      toast.success(`Excel file exported successfully! (${data.length} rows)`, { autoClose: 2000 });
    } catch (error) {
      console.error('Error exporting Excel:', error);
      toast.error('Error exporting Excel file. Please try again.', { autoClose: 3000 });
    }
  };

  // Dropdown toggle handler
  const handleSaveDropdownToggle = () => {
    setShowSaveDropdown((prev) => !prev);
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    if (!showSaveDropdown) return;
    const handleClick = (e) => {
      if (!e.target.closest('.save-dropdown-wrapper')) {
        setShowSaveDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showSaveDropdown]);

  return (
    <div className="printable-table-wrapper">
      {showOptions && (
      <div className="printable-actions no-print">
          <div className="action-buttons">
            {/* Select Fields Button */}
            <button
              onClick={() => setShowFieldsModal(true)}
              className="printable-action-btn"
              title="Select Fields"
            >
              <BsColumnsGap />
            </button>
            {/* Save Dropdown */}
            <div className="save-dropdown-wrapper">
              <button
                onClick={handleSaveDropdownToggle}
                className="printable-action-btn"
                title="Export"
                aria-haspopup="true"
                aria-expanded={showSaveDropdown}
              >
                <AiOutlineDownload />
              </button>
              {showSaveDropdown && (
                <div className="save-dropdown-menu">
                  <button
                    className="dropdown-item"
                    onClick={() => handleExportWithPrompt('pdf')}
                  >
                    Export as PDF
                  </button>
                  <button
                    className="dropdown-item"
                    onClick={() => handleExportWithPrompt('excel')}
                  >
                    Export as Excel
                  </button>
                </div>
              )}
            </div>
            {/* Print and Settings remain unchanged */}
            <button 
              onClick={handlePrint}
              className="printable-action-btn"
              title="Print Table"
            >
              <AiOutlinePrinter />
            </button>
            <button 
              onClick={() => setShowSettings(!showSettings)}
              className="printable-action-btn settings-btn"
              title={`Export Settings (${pdfSettings.orientation}, ${pdfSettings.format}, ${pdfSettings.fontSize}pt)`}
            >
              <AiOutlineSetting />
            </button>
          </div>
          
          {/* Export Settings Panel */}
          {showSettings && (
            <div className="export-settings">
              <h4>Export Settings</h4>
              <div className="settings-grid">
                <div className="setting-group">
                  <label htmlFor="orientation">Orientation:</label>
                  <select
                    id="orientation"
                    value={pdfSettings.orientation}
                    onChange={(e) => setPdfSettings(prev => ({ ...prev, orientation: e.target.value }))}
                  >
                    <option value="portrait">Portrait</option>
                    <option value="landscape">Landscape</option>
                  </select>
                </div>
                
                <div className="setting-group">
                  <label htmlFor="format">Page Format:</label>
                  <select
                    id="format"
                    value={pdfSettings.format}
                    onChange={(e) => setPdfSettings(prev => ({ ...prev, format: e.target.value }))}
                  >
                    <option value="a4">A4</option>
                    <option value="letter">Letter</option>
                    <option value="legal">Legal</option>
                  </select>
                </div>
                
                <div className="setting-group">
                  <label htmlFor="fontSize">Font Size:</label>
                  <select
                    id="fontSize"
                    value={pdfSettings.fontSize}
                    onChange={(e) => setPdfSettings(prev => ({ ...prev, fontSize: parseInt(e.target.value) }))}
                  >
                    <option value={8}>8pt</option>
                    <option value={10}>10pt</option>
                    <option value={12}>12pt</option>
                    <option value={14}>14pt</option>
                  </select>
                </div>
                
                <div className="setting-group">
                  <label htmlFor="maxRowsPerPage">Max Rows per Page:</label>
                  <select
                    id="maxRowsPerPage"
                    value={pdfSettings.maxRowsPerPage}
                    onChange={(e) => setPdfSettings(prev => ({ ...prev, maxRowsPerPage: parseInt(e.target.value) }))}
                  >
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={75}>75</option>
                    <option value={100}>100</option>
                  </select>
                </div>
                
                <div className="setting-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={pdfSettings.includeTitle}
                      onChange={(e) => setPdfSettings(prev => ({ ...prev, includeTitle: e.target.checked }))}
                    />
                    Include Title
                  </label>
                </div>
                
                <div className="setting-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={pdfSettings.includeDate}
                      onChange={(e) => setPdfSettings(prev => ({ ...prev, includeDate: e.target.checked }))}
                    />
                    Include Date
                  </label>
                </div>
              </div>
            </div>
          )}
          {/* File name prompt modal */}
          {showFileNamePrompt && (
            <div className="file-name-prompt-backdrop">
              <div className="file-name-prompt-modal" style={{ position: 'relative' }}>
                <button 
                  type="button" 
                  className="modal-close-btn"
                  onClick={() => setShowFileNamePrompt(false)}
                  style={{
                    position: 'absolute',
                    top: '10px',
                    right: '15px',
                    background: 'none',
                    border: 'none',
                    fontSize: '22px',
                    cursor: 'pointer',
                    color: '#666',
                    fontWeight: 'bold',
                    lineHeight: '1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0
                  }}
                  aria-label="Close"
                >
                  <AiOutlineClose />{' '}
                </button>
                <form onSubmit={handleFileNameSubmit}>
                  <label htmlFor="exportFileName">Enter file name:</label>
                  <input
                    id="exportFileName"
                    type="text"
                    value={customFileName}
                    onChange={e => setCustomFileName(e.target.value)}
                    autoFocus
                    placeholder={pendingExportType === 'pdf' ? 'customers.pdf' : 'customers.csv'}
                    className="file-name-input"
                  />
                  <div className="file-name-prompt-actions">
                    <button type="submit" className="btn-save">Save</button>
                  </div>
                </form>
              </div>
            </div>
          )}
          {/* Fields selection modal */}
          {showFieldsModal && (
            <div className="file-name-prompt-backdrop">
              <div className="file-name-prompt-modal">
                <button 
                  type="button" 
                  className="modal-close-btn"
                  onClick={() => setShowFieldsModal(false)}
                  style={{
                    position: 'absolute',
                    top: '10px',
                    right: '15px',
                    background: 'none',
                    border: 'none',
                    fontSize: '20px',
                    cursor: 'pointer',
                    color: '#666',
                    fontWeight: 'bold',
                    lineHeight: '1'
                  }}
                >
                  ×
                </button>
                <form onSubmit={e => { e.preventDefault(); setShowFieldsModal(false); }}>
                  <label style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Select fields to include:</label>
                  <div className="fields-checkbox-list">
                    {allFields.map(field => (
                      <label key={field.key} className="fields-checkbox-item">
                        <input
                          type="checkbox"
                          checked={selectedFields.includes(field.key)}
                          onChange={e => {
                            const { checked } = e.target;
                            setSelectedFields(prev => {
                              if (checked) {
                                const next = new Set([...prev, field.key]);
                                return Array.from(next);
                              }
                              if (prev.length <= 1) {
                                toast.warning('Select at least one field.', { autoClose: 1500 });
                                return prev;
                              }
                              return prev.filter(key => key !== field.key);
                            });
                          }}
                        />
                        {field.label}
                      </label>
                    ))}
                  </div>
                  <div className="file-name-prompt-actions">
                    <button type="button" onClick={() => setShowFieldsModal(false)} className="btn-save">Close</button>
                  </div>
                </form>
              </div>
            </div>
          )}
      </div>
      )}
      <div ref={containerRef} className="printable-table-content">
        {children}
      </div>
      <ToastContainer
        position="top-center"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </div>
  );
}
